import { useCallback, useEffect, useState } from "react";
import { useLatestRequest } from "../../../../hooks/useLatestRequest";
import { getApiErrorMessage } from "../../../../lib/result-codes";
import { getMerchantCouponList } from "../api/merchantCouponList.api";
import {
  mapApiResponseToUI,
  type MerchantCouponListFilter,
  type MerchantCouponListUIState,
} from "../data/merchantCouponList.data";

interface MerchantCouponListPagination {
  index: number;
  size: number;
  count: number;
}

interface UseGetMerchantCouponListReturn {
  coupons: MerchantCouponListUIState[];
  loading: boolean;
  error: string | null;
  filters: MerchantCouponListFilter;
  updateFilter: (filter: Partial<MerchantCouponListFilter>) => void;
  clearFilters: () => void;
  pagination: MerchantCouponListPagination;
  setPagination: (pagination: { index: number; size?: number }) => void;
  refreshCoupons: () => Promise<void>;
}

const DEFAULT_PAGINATION: MerchantCouponListPagination = {
  index: 1,
  size: 20,
  count: 0,
};

export const useGetMerchantCouponList = (): UseGetMerchantCouponListReturn => {
  const [coupons, setCoupons] = useState<MerchantCouponListUIState[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<MerchantCouponListFilter>({});
  const [pagination, setPaginationState] =
    useState<MerchantCouponListPagination>(DEFAULT_PAGINATION);
  const { run: runLatestRequest, loading } = useLatestRequest();

  const fetchCoupons = useCallback(async () => {
    try {
      setError(null);
      const response = await runLatestRequest((signal) =>
        getMerchantCouponList(
          {
            filter: filters,
            pagination: {
              index: pagination.index - 1,
              size: pagination.size,
            },
          },
          signal,
        ),
      );

      if (!response) return;
      setCoupons((response.list ?? []).map(mapApiResponseToUI));
      setPaginationState((current) => ({
        ...current,
        count: response.paging?.count ?? 0,
      }));
    } catch (fetchError: unknown) {
      setCoupons([]);
      setError(getApiErrorMessage(fetchError, "Failed to load coupons"));
    }
  }, [filters, pagination.index, pagination.size, runLatestRequest]);

  useEffect(() => {
    void fetchCoupons();
  }, [fetchCoupons]);

  const updateFilter = useCallback((filter: Partial<MerchantCouponListFilter>) => {
    setFilters((current) => ({ ...current, ...filter }));
    setPaginationState((current) => ({ ...current, index: 1 }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({});
    setPaginationState((current) => ({ ...current, index: 1 }));
  }, []);

  const setPagination = useCallback((next: { index: number; size?: number }) => {
    setPaginationState((current) => ({
      ...current,
      index: next.index,
      ...(next.size ? { size: next.size } : {}),
    }));
  }, []);

  return {
    coupons,
    loading,
    error,
    filters,
    updateFilter,
    clearFilters,
    pagination,
    setPagination,
    refreshCoupons: fetchCoupons,
  };
};
